import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { ButtonContainer, Container } from './FeedbackModal.styled';
import { Button } from 'components/v2/Buttons/Button/Button';
import { TextArea } from 'components/v2/Inputs/TextArea/TextArea';
import { Modal } from 'components/v2/Modal/Modal';
import { Text } from 'components/v2/Typography/Text';
import { usePatient } from 'hooks/usePatient/usePatient';
import theme from 'kb-shared/theme';
import KBContacts from 'kb-shared/utilities/kindbody_contacts';
import { showErrorToast, showSuccessToast } from 'utilities/notificationUtils';

import { CREATE_PATIENT_FEEDBACK } from './FeedbackModal.graphql';

export const FeedbackModal = ({
  title,
  open,
  close,
  id
}: {
  title: string;
  open: boolean;
  close: () => void;
  id: string;
}) => {
  const { patient } = usePatient();
  const [value, setValue] = useState('');

  const [createFeedback, { loading }] = useMutation(CREATE_PATIENT_FEEDBACK, {
    onCompleted: () => {
      setValue('');
      showSuccessToast('Thank you for your feedback!');
      close();
    },
    onError: () => {
      showErrorToast(
        `Failed to send feedback. Please wait then try again, or contact ${KBContacts.navigatorEmail}`
      );
    }
  });

  const handleSend = () => {
    if (loading) return;
    if (!value.trim()) return;

    createFeedback({
      variables: {
        marketId: Number(patient?.lab?.id),
        employerId: patient?.patientMembership?.employer
          ? Number(patient?.patientMembership?.employer)
          : null,
        text: value
      }
    });
  };

  return (
    <Modal title={title} open={open} onCancel={close} maxWidth={800}>
      <Container>
        <Text size={'md'} color={theme.colors.neutral.primaryNavy} fontStyle="regular">
          We value your input! Please share your thoughts below to help us improve your
          experience.{' '}
        </Text>
        <TextArea
          height="290px"
          id={`feedback-textarea-${id}`}
          placeholder="Start typing..."
          value={value}
          onChange={e => {
            setValue(e.currentTarget.value);
          }}
        />
        <ButtonContainer>
          <Button fullWidth category="secondary" size="lg" label="Cancel" onClick={close} />
          <Button
            fullWidth
            category="primary"
            size="lg"
            label="Send"
            isDisabled={!value.trim() || loading}
            onClick={handleSend}
          />
        </ButtonContainer>
      </Container>
    </Modal>
  );
};
